import React, { useEffect, useState } from 'react';
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../utils/i18n/i18n";
import { STRING_LANGUAGE_ULTIMA_MILLA } from '../../utils/const/string-const';

export default function PrivacyPolicyPage() {
    const history = useHistory();
    const { t } = useTranslation();
    const [selectedLanguage, setSelectedLanguage] = useState('es');
    const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);

    const languages = [
        { value: 'en', label: 'English', flag: '🇺🇸' },
        { value: 'es', label: 'Español', flag: '🇪🇸' },
        { value: 'pt', label: 'Português', flag: '🇧🇷' }
    ];

    const policySections = [
        { titleKey: 'label_politica_seccion_servicio_titulo', textKey: 'label_politica_seccion_servicio_texto' },
        { titleKey: 'label_politica_seccion_proveedor_titulo', textKey: 'label_politica_seccion_proveedor_texto' },
        { titleKey: 'label_politica_seccion_responsabilidad_titulo', textKey: 'label_politica_seccion_responsabilidad_texto' },
        { titleKey: 'label_politica_seccion_uso_usuario_titulo', textKey: 'label_politica_seccion_uso_usuario_texto' },
        { titleKey: 'label_politica_seccion_cambios_titulo', textKey: 'label_politica_seccion_cambios_texto' },
        { titleKey: 'label_politica_seccion_privacidad_titulo', textKey: 'label_politica_seccion_privacidad_texto' },
        { titleKey: 'label_politica_seccion_aceptacion_titulo', textKey: 'label_politica_seccion_aceptacion_texto' },
    ];

    useEffect(() => {
        const language = localStorage.getItem(STRING_LANGUAGE_ULTIMA_MILLA);
        if (language) {
            i18n.changeLanguage(language);
            setSelectedLanguage(language);
        } else {
            i18n.changeLanguage('en');
            setSelectedLanguage('en');
        }
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isLanguageDropdownOpen && !event.target.closest('.language-dropdown')) {
                setIsLanguageDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isLanguageDropdownOpen]);

    const LanguageDropdown = () => (
        <div className="relative language-dropdown">
            <button
                onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition"
            >
                <span className="text-xl">{languages.find(lang => lang.value === selectedLanguage)?.flag}</span>
                <span className="text-gray-700 text-sm font-medium">{languages.find(lang => lang.value === selectedLanguage)?.label}</span>
                <svg
                    className={`w-4 h-4 text-gray-500 transition-transform ${isLanguageDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {isLanguageDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                    {languages.map((language) => (
                        <button
                            key={language.value}
                            onClick={() => {
                                setSelectedLanguage(language.value);
                                setIsLanguageDropdownOpen(false);
                                i18n.changeLanguage(language.value);
                                localStorage.setItem(STRING_LANGUAGE_ULTIMA_MILLA, language.value);
                            }}
                            className={`w-full flex items-center space-x-3 px-4 py-2 hover:bg-blue-50 transition ${selectedLanguage === language.value ? 'bg-blue-50 text-blue-600' : 'text-gray-700'
                                }`}
                        >
                            <span className="text-xl">{language.flag}</span>
                            <span className="font-medium">{language.label}</span>
                            {selectedLanguage === language.value && (
                                <svg className="w-4 h-4 ml-auto text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );

    const render = () => {
        try {
            const currentYear = new Date().getFullYear();
            const lastUpdated = 'May 26, 2026';

            return (
                <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
                    <header className="bg-white shadow-md sticky top-0 z-50">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            <div className="flex justify-between items-center py-4">
                                <div className="flex items-center">
                                    <h1 className="text-2xl font-bold text-blue-600">DataTerra</h1>
                                    <span className="ml-2 text-sm text-gray-500">{t("label_tms")}</span>
                                </div>
                                <div className="flex items-center space-x-4">
                                    <div className="hidden md:block">
                                        <LanguageDropdown />
                                    </div>
                                    <button
                                        onClick={() => history.push('/landing')}
                                        className="hidden sm:inline text-gray-700 hover:text-blue-600 transition text-sm font-medium"
                                    >
                                        {t("label_politica_volver_landing")}
                                    </button>
                                    <button
                                        onClick={() => history.push('/login')}
                                        className="text-blue-600 hover:text-blue-700 font-medium text-sm md:text-base"
                                    >
                                        {t("label_iniciar_sesion")}
                                    </button>
                                </div>
                            </div>
                            <div className="md:hidden border-t border-gray-200 py-3 flex justify-center">
                                <LanguageDropdown />
                            </div>
                        </div>
                    </header>

                    <main className="py-12 px-4 sm:px-6 lg:px-8">
                        <div className="max-w-4xl mx-auto">
                            <div className="text-center mb-10">
                                <span className="inline-block bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                                    {t("label_tms")}
                                </span>
                                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                                    {t("label_politica_de_privacidad")}
                                </h1>
                                <p className="text-gray-500 text-sm">
                                    {t("label_ultima_actualizacion")}: {lastUpdated}
                                </p>
                            </div>

                            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 md:p-12">
                                <p className="text-lg text-gray-700 leading-relaxed mb-10 pb-8 border-b border-gray-200">
                                    {t("label_politica_intro")}
                                </p>

                                <div className="space-y-10">
                                    {policySections.map((section, index) => (
                                        <section key={index}>
                                            <h2 className="text-xl font-bold text-gray-900 mb-3">
                                                {t(section.titleKey)}
                                            </h2>
                                            <p className="text-gray-600 leading-relaxed">
                                                {t(section.textKey)}
                                            </p>
                                        </section>
                                    ))}
                                </div>
                            </div>

                            <div className="mt-8 flex flex-col sm:flex-row justify-center items-center gap-4">
                                <button
                                    onClick={() => history.push('/landing')}
                                    className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-50 transition shadow-lg border-2 border-blue-600"
                                >
                                    {t("label_politica_volver_landing")}
                                </button>
                                <button
                                    onClick={() => history.push('/sign-up')}
                                    className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition shadow-lg"
                                >
                                    {t("label_comenzar_ahora")}
                                </button>
                            </div>
                        </div>
                    </main>

                    <footer className="bg-gray-900 text-gray-300 py-12 px-4 sm:px-6 lg:px-8">
                        <div className="max-w-7xl mx-auto">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <h3 className="text-white text-lg font-bold mb-4">DataTerra</h3>
                                    <p className="text-sm text-gray-400">{t("label_tms")}</p>
                                </div>
                                {/* <div>
                                    <h4 className="text-white font-semibold mb-4">{t("label_contacto")}</h4>
                                    <ul className="space-y-2 text-sm">
                                        <li>📧 info@boltrack.com</li>
                                        <li>📞 +591 67701920</li>
                                    </ul>
                                </div> */}
                            </div>
                            <div className="border-t border-gray-800 mt-8 pt-8 text-sm text-center text-gray-500">
                                <p>© {currentYear} DataTerra. {t("label_politica_de_privacidad")}.</p>
                            </div>
                        </div>
                    </footer>
                </div>
            );
        } catch (err) {
            return (
                <div className="min-h-screen flex items-center justify-center bg-red-50">
                    <div className="text-center">
                        <h1 className="text-2xl font-bold text-red-600 mb-4">Oops! Algo salió mal</h1>
                        <p className="text-gray-600">{err.message}</p>
                    </div>
                </div>
            );
        }
    };

    return render();
}
